<template>
  <div class="page dashboard-page">
    <div class="dashboard-head">
      <div>
        <h2 class="page-title">{{ $t('menu.dashboard') }}</h2>
        <div class="muted dashboard-subtitle">{{ userLine }}</div>
      </div>
    </div>

    <div class="dashboard-status glass-card">
      <div class="dashboard-status__copy">
        <div class="dashboard-status__label">{{ $t('dashboard.notifications') }}</div>
        <div class="dashboard-status__text">{{ notificationStatusText }}</div>
      </div>
      <el-button
        size="small"
        type="primary"
        class="btn-glass dashboard-status__button"
        plain
        @click="enableNotifications"
        :disabled="notificationStatus === 'granted'"
      >
        <i class="el-icon-bell" />
        {{ notificationButtonText }}
      </el-button>
    </div>

    <div class="overview-grid dashboard-grid">
      <div
        v-for="card in cards"
        :key="card.key"
        :class="['overview-tile', 'glass-card', card.tone]"
        role="button"
        tabindex="0"
        @click="openCard(card)"
        @keyup.enter="openCard(card)"
      >
        <div class="overview-tile__top">
          <div class="overview-tile__icon"><i :class="card.icon" /></div>
          <span :class="['tag-pill', 'overview-tile__badge', card.warn ? 'tag-pill--warn' : '']">
            {{ card.badge }}
          </span>
        </div>
        <div class="overview-tile__body">
          <div class="overview-tile__metric">{{ card.metric }}</div>
          <div class="overview-tile__title">{{ card.title }}</div>
          <div class="muted overview-tile__desc">{{ card.desc }}</div>
        </div>
        <i class="el-icon-arrow-right overview-tile__arrow" />
      </div>
    </div>
  </div>
</template>

<script>
import { requestNotificationPermission } from '../services/attention';

const MAX_BADGE = 99;

export default {
  name: 'Dashboard',
  data() {
    return {
      notificationStatus: typeof window.Notification !== 'undefined' ? window.Notification.permission : 'denied'
    };
  },
  computed: {
    role() {
      return this.$store.getters.role;
    },
    userLine() {
      const u = this.$store.state.user;
      if (!u) return '-';
      const roleLabel = this.$t(`role.${u.role}`);
      return `${u.displayName} / ${u.username} / ${roleLabel}`;
    },
    notificationButtonText() {
      if (this.notificationStatus === 'granted') return this.$t('dashboard.notificationsEnabled');
      if (this.notificationStatus === 'denied') return this.$t('dashboard.notificationsBlocked');
      return this.$t('dashboard.enableNotifications');
    },
    notificationStatusText() {
      if (this.notificationStatus === 'granted') return this.$t('dashboard.notificationsGranted');
      if (this.notificationStatus === 'denied') return this.$t('dashboard.notificationsDenied');
      return this.$t('dashboard.notificationsDefault');
    },
    mustConfirmAnnouncement() {
      return !!this.$store.state.mustConfirmAnnouncement;
    },
    ticketBadge() {
      const redDot = Number(this.$store.state.ticketRedDotCount || 0);
      if (redDot > 0) return redDot;
      return Number(this.$store.state.ticketNeedMoreCount || 0) + Number(this.$store.state.ticketClosedNoticeCount || 0);
    },
    cards() {
      if (this.role === 'USER') return this.userCards;
      if (this.role === 'HANDLER') return this.handlerCards;
      if (this.role === 'SECONDARY_HANDLER') return this.secondaryCards;
      if (this.role === 'ADMIN') return this.adminCards;
      return [];
    },
    userCards() {
      const announcementPending = this.mustConfirmAnnouncement ? 1 : 0;
      return [
        {
          key: 'tickets',
          icon: 'el-icon-document',
          tone: 'overview-tile--blue',
          metric: this.formatCount(this.ticketBadge),
          badge: this.formatBadge(this.ticketBadge),
          title: this.$t('menu.tickets'),
          desc: this.$t('dashboard.ticketsHint'),
          to: '/tickets',
          warn: this.ticketBadge > 0
        },
        {
          key: 'tasks',
          icon: 'el-icon-s-claim',
          tone: 'overview-tile--cyan',
          metric: this.formatCount(this.$store.state.taskRedDotCount),
          badge: this.formatBadge(this.$store.state.taskRedDotCount),
          title: this.$t('menu.tasks'),
          desc: this.$t('dashboard.tasksHint'),
          to: '/tasks',
          warn: Number(this.$store.state.taskRedDotCount || 0) > 0
        },
        {
          key: 'announcements',
          icon: 'el-icon-bell',
          tone: 'overview-tile--slate',
          metric: announcementPending > 0 ? this.formatCount(announcementPending) : '-',
          badge: announcementPending > 0 ? this.$t('announcement.required') : this.$t('announcement.history'),
          title: this.$t('menu.announcements'),
          desc: announcementPending > 0 ? this.$t('dashboard.announcementsRequiredHint') : this.$t('dashboard.announcementsHistoryHint'),
          to: announcementPending > 0 ? '/announcements/confirm' : '/user/announcements',
          warn: announcementPending > 0
        }
      ];
    },
    handlerCards() {
      const pool = Number(this.$store.state.handlerBadges.pool || 0);
      const disputes = Number(this.$store.state.handlerBadges.disputes || 0);
      return [
        {
          key: 'handler-pool',
          icon: 'el-icon-collection-tag',
          tone: 'overview-tile--cyan',
          metric: this.formatCount(pool),
          badge: this.formatBadge(pool),
          title: this.$t('menu.handlerPool'),
          desc: this.$t('dashboard.handlerPoolHint'),
          to: '/handler/pool',
          warn: pool > 0
        },
        {
          key: 'handler-in-progress',
          icon: 'el-icon-loading',
          tone: 'overview-tile--blue',
          metric: '-',
          badge: this.$t('dashboard.active'),
          title: this.$t('menu.handlerInProgress'),
          desc: this.$t('dashboard.handlerInProgressHint'),
          to: '/handler/in-progress'
        },
        {
          key: 'handler-disputes',
          icon: 'el-icon-warning-outline',
          tone: 'overview-tile--orange',
          metric: this.formatCount(disputes),
          badge: this.formatBadge(disputes),
          title: this.$t('menu.handlerDisputes'),
          desc: this.$t('dashboard.handlerDisputesHint'),
          to: '/handler/disputes',
          warn: disputes > 0
        }
      ];
    },
    secondaryCards() {
      const badges = this.$store.state.secondaryBadges || {};
      const pending = Number(badges.pendingCases || 0);
      const submitted = Number(badges.submittedCases || 0);
      const closedNotice = Number(badges.closedNotice || 0);
      const secondaryDisputes = Number(badges.secondaryDisputes || 0);
      const taskRedDot = Number(badges.taskRedDot || 0);
      const mustConfirmAnnouncement = !!badges.mustConfirmAnnouncement;
      const work = pending + secondaryDisputes;
      return [
        {
          key: 'secondary-submitted',
          icon: 'el-icon-edit-outline',
          tone: 'overview-tile--blue',
          metric: this.formatCount(submitted),
          badge: this.formatBadge(submitted),
          title: this.$t('menu.tickets'),
          desc: this.$t('dashboard.secondarySubmitHint'),
          to: '/secondary/submit',
          warn: submitted > 0
        },
        {
          key: 'secondary-work',
          icon: 'el-icon-loading',
          tone: 'overview-tile--cyan',
          metric: this.formatCount(work),
          badge: this.formatBadge(work),
          title: this.$t('menu.handlerInProgress'),
          desc: this.$t('dashboard.secondaryPendingHint'),
          to: '/secondary/pending',
          warn: work > 0
        },
        {
          key: 'secondary-closed',
          icon: 'el-icon-circle-check',
          tone: 'overview-tile--slate',
          metric: this.formatCount(closedNotice),
          badge: this.formatBadge(closedNotice),
          title: this.$t('menu.secondaryClosed'),
          desc: this.$t('dashboard.secondaryClosedHint'),
          to: '/secondary/closed',
          warn: closedNotice > 0
        },
        {
          key: 'secondary-tasks',
          icon: 'el-icon-bell',
          tone: 'overview-tile--orange',
          metric: this.formatCount(taskRedDot),
          badge: this.formatBadge(taskRedDot),
          title: this.$t('menu.tasks'),
          desc: this.$t('dashboard.secondaryTasksHint'),
          to: '/secondary/tasks',
          warn: taskRedDot > 0
        },
        {
          key: 'secondary-announcements',
          icon: 'el-icon-message',
          tone: 'overview-tile--violet',
          metric: this.formatCount(mustConfirmAnnouncement ? 1 : 0),
          badge: this.formatBadge(mustConfirmAnnouncement ? 1 : 0),
          title: this.$t('menu.announcements'),
          desc: mustConfirmAnnouncement ? this.$t('dashboard.secondaryAnnouncementsRequiredHint') : this.$t('dashboard.secondaryAnnouncementsHistoryHint'),
          to: mustConfirmAnnouncement ? '/announcements/confirm' : '/secondary/announcements',
          warn: mustConfirmAnnouncement
        }
      ];
    },
    adminCards() {
      const taskBadge = Number(this.$store.state.adminBadges.tasks || 0);
      const announcementBadge = Number(this.$store.state.adminBadges.announcements || 0);
      return [
        {
          key: 'admin-users',
          icon: 'el-icon-s-custom',
          tone: 'overview-tile--blue',
          metric: '-',
          badge: this.$t('dashboard.manage'),
          title: this.$t('menu.adminUsers'),
          desc: this.$t('dashboard.adminUsersHint'),
          to: '/admin/users'
        },
        {
          key: 'admin-tasks',
          icon: 'el-icon-s-order',
          tone: 'overview-tile--cyan',
          metric: this.formatCount(taskBadge),
          badge: this.formatBadge(taskBadge),
          title: this.$t('menu.adminTasks'),
          desc: this.$t('dashboard.adminTasksHint'),
          to: '/admin/tasks',
          warn: taskBadge > 0
        },
        {
          key: 'admin-announcements',
          icon: 'el-icon-message-solid',
          tone: 'overview-tile--slate',
          metric: this.formatCount(announcementBadge),
          badge: this.formatBadge(announcementBadge),
          title: this.$t('menu.adminAnnouncements'),
          desc: this.$t('dashboard.adminAnnouncementsHint'),
          to: '/admin/announcements',
          warn: announcementBadge > 0
        },
        {
          key: 'admin-kpi',
          icon: 'el-icon-data-analysis',
          tone: 'overview-tile--violet',
          metric: 'KPI',
          badge: this.$t('dashboard.report'),
          title: this.$t('menu.kpi'),
          desc: this.$t('dashboard.kpiHint'),
          to: '/kpi'
        },
        {
          key: 'admin-system',
          icon: 'el-icon-setting',
          tone: 'overview-tile--orange',
          metric: '60d',
          badge: this.$t('dashboard.system'),
          title: this.$t('menu.adminSystem'),
          desc: this.$t('dashboard.adminSystemHint'),
          to: '/admin/system'
        }
      ];
    }
  },
  methods: {
    formatCount(value) {
      const n = Number(value || 0);
      if (n > MAX_BADGE) return `${MAX_BADGE}+`;
      return String(n);
    },
    formatBadge(value) {
      const n = Number(value || 0);
      if (n <= 0) return '0';
      if (n > MAX_BADGE) return `${MAX_BADGE}+`;
      return String(n);
    },
    openCard(card) {
      if (!card || !card.to) return;
      this.$router.push(card.to);
    },
    enableNotifications() {
      if (typeof window.Notification === 'undefined') {
        this.$message.warning(this.$t('dashboard.notificationsNotSupported'));
        return;
      }
      requestNotificationPermission().then(result => {
        this.notificationStatus = result;
        if (result === 'granted') {
          this.$message.success(this.$t('dashboard.notificationsGranted'));
        } else if (result === 'denied') {
          this.$message.warning(this.$t('dashboard.notificationsDenied'));
        }
      });
    }
  }
};
</script>

<style scoped>
.dashboard-page {
  --tile-h: 146px;
}
.dashboard-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}
.dashboard-subtitle {
  font-size: 13px;
  max-width: 520px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dashboard-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  margin-bottom: 14px;
  min-height: 44px;
  box-sizing: border-box;
}
.dashboard-status__copy {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}
.dashboard-status__label {
  font-weight: 600;
  flex: 0 0 auto;
}
.dashboard-status__text {
  min-width: 0;
  color: var(--muted);
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dashboard-status__button {
  flex: 0 0 auto;
}
.dashboard-status__button i {
  margin-right: 4px;
}
.dashboard-grid {
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
}
.overview-tile {
  min-height: var(--tile-h);
  aspect-ratio: auto;
  box-sizing: border-box;
}
.overview-tile__body {
  min-width: 0;
}
.overview-tile__metric {
  margin-top: 12px;
  font-size: 28px;
  line-height: 1;
  font-weight: 700;
  letter-spacing: 0;
  font-variant-numeric: tabular-nums;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overview-tile__badge {
  max-width: 92px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overview-tile__title {
  max-width: calc(100% - 22px);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overview-tile__desc {
  min-height: 36px;
  max-height: 38px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.overview-tile__arrow {
  position: absolute;
  right: 16px;
  bottom: 16px;
  color: rgba(81, 96, 118, 0.75);
}
@media (max-width: 680px) {
  .dashboard-head,
  .dashboard-status {
    flex-direction: column;
    align-items: stretch;
  }
  .dashboard-status__copy {
    align-items: flex-start;
    flex-direction: column;
    gap: 4px;
  }
  .dashboard-status__text,
  .dashboard-subtitle {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
}
</style>
